import React, { useState } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { nowIso } from "../../../workflow";

type Props = {
  onClose: () => void;
};

export const EditProfileModal: React.FC<Props> = ({ onClose }) => {
  const { selectedProfile, patchState } = useMonyawn();
  const [skills, setSkills] = useState(selectedProfile?.skills_summary ?? "");
  const [level, setLevel] = useState(selectedProfile?.experience_level ?? "");
  const [strengths, setStrengths] = useState(selectedProfile?.domain_strengths ?? "");
  const [gaps, setGaps] = useState(selectedProfile?.declared_gaps ?? "");

  if (!selectedProfile) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    patchState(
      (current) => ({
        ...current,
        candidateProfiles: current.candidateProfiles.map((p) =>
          p.profile_id === selectedProfile!.profile_id
            ? {
                ...p,
                skills_summary: skills,
                experience_level: level,
                domain_strengths: strengths,
                declared_gaps: gaps,
                user_corrected: true,
                updated_at: nowIso(),
              }
            : p,
        ),
      }),
      "Profile updated.",
    );
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-5 animate-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-accent">
              Your profile
            </p>
            <h2 className="text-xl font-bold text-brand-ink mt-1">Make it sound like you</h2>
          </div>
          <button
            onClick={onClose}
            className="text-brand-muted hover:text-brand-ink transition-colors mt-1"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Skills summary
            </span>
            <textarea
              className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
              rows={3}
              placeholder="What do you actually know and do well?"
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                Level
              </span>
              <input
                className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                placeholder="Senior, Staff, IC4…"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                Strengths
              </span>
              <textarea
                className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
                rows={2}
                placeholder="Where you're genuinely ahead"
                value={strengths}
                onChange={(e) => setStrengths(e.target.value)}
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Gaps to cover
            </span>
            <textarea
              className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
              rows={3}
              placeholder="What the role requires that you're still building. Be honest — we'll address it."
              value={gaps}
              onChange={(e) => setGaps(e.target.value)}
            />
          </label>

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              className="flex-1 bg-brand-accent text-white py-3 rounded-2xl font-bold hover:bg-brand-accent-strong transition-all shadow-sm active:scale-95"
            >
              Save profile
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-brand-surface border border-black/10 text-brand-ink px-6 py-3 rounded-2xl font-medium hover:bg-black/5 transition-all active:scale-95"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
